/**
 * Permissions catalog — single source of truth for both api (CASL ability
 * builder + DB seeder) and web (UI gating + role-edit dropdowns).
 *
 * Adding a permission: extend `PERMISSIONS` (and `ACTIONS` / `SUBJECTS` /
 * `SUBJECT_GROUP` as needed) here — this file is the only place it is declared.
 * The `permissions` table is written by `CatalogSeeder`
 * (apps/api/src/shared/panel-db/seeders/catalog.seeder.ts) and by nothing else:
 * no migration seeds it, and the api does NOT reconcile catalog against DB at
 * boot. Until someone runs `bun run db:seed` on that environment the new
 * permission has no row, so no role can be granted it (`role.repository.ts`
 * throws `PermissionNotCataloguedError`) and every operator whose page needs it
 * is gated out. A hand-run seeds whatever `packages/shared/dist` currently
 * holds, so build `@tp/shared` first. See docs/deploy/cutover-runbook.md.
 *
 * The contract tests in
 * apps/api/src/shared/auth/test/unit/permissions.contract.test.ts fail the build
 * when the catalog is internally inconsistent.
 *
 * Action vocabulary: 'manage' is the wildcard (implies all other actions).
 * Otherwise we use CRUD-shaped verbs ('read', 'create', 'update', 'delete')
 * plus a small number of domain-specific verbs ('assign', 'annotate').
 */

export const ACTIONS = [
  'manage',
  'read',
  'create',
  'update',
  'delete',
  'assign',
  'annotate',
  'make',
  'clear',
  'grantTest',
  'grant',
  'consume',
  'close',
  'kick',
  // Invisible-listener whitelist (ADR 0049). Its own verb rather than `update`,
  // so a future moderator_admin can hold it without the sip_rooms registry.
  'manageStealth',
  'reorder',
  'label',
  // Conference console. `moderate` covers mute/kick/ban on the IVR side and
  // gates full phone-number visibility; `listen` and `broadcast` are separate
  // because hearing and speaking into a live room are distinct escalations.
  'moderate',
  'listen',
  'broadcast',
  // Unmask a reported phone caller's number (moderation reports). Its own verb
  // because every reveal is audited and a moderator can decide a report
  // without ever seeing the number.
  'reveal',
] as const;
export type Action = (typeof ACTIONS)[number];

export const SUBJECTS = [
  'all',
  'Vip',
  'Sms',
  'Otp',
  'Ftu',
  'Ban',
  'Balance',
  'VoiceRecord',
  'VoiceRoom',
  'CallMonitor',
  'UserMonitor',
  'Allowances',
  'Interest',
  'Horoscope',
  'TextFilter',
  'Cosmetic',
  'CoinHistory',
  'Game',
  'RevenuecatSync',
  'RevenuecatConsole',
  'Announcement',
  'MessageTemplate',
  'MessageAutomation',
  'User',
  'StaffUser',
  'Role',
  'Audit',
  'SystemConfig',
  'SyntheticUsers',
  'Quest',
  'Spotlight',
  'Shop',
  'Gift',
  'ProfileImage',
  'Report',
  'Conference',
  'Restriction',
  'RoomIncident',
  'UserReport',
  'LiveOps',
] as const;
export type Subject = (typeof SUBJECTS)[number];

/** Per-subject allowed actions. UI dropdowns and seed scripts iterate this. */
export const PERMISSIONS: Readonly<Record<Subject, readonly Action[]>> = Object.freeze({
  all: ['manage'],                                                    // wildcard
  Vip: ['read', 'create', 'delete'],                                  // VIPs aren't edited
  Sms: ['read'],
  Otp: ['read'],
  Ftu: ['read', 'make', 'clear'],
  Ban: ['read', 'create', 'update', 'delete'],
  Balance: ['read', 'update'],
  VoiceRecord: ['read', 'annotate'],
  // Voice-room monitoring (tc-fun): read live/history; close = force-close a
  // room; update = tune the sip_rooms IVR routing registry (cap/priority/status);
  // kick = eject one member with a time-boxed rejoin cooldown; manageStealth =
  // read and edit the invisible-listener whitelist (ADR 0049).
  VoiceRoom: ['read', 'close', 'update', 'kick', 'manageStealth'],
  // Live 1-on-1 call presence monitoring (tc-fun): read-only (no mutations).
  CallMonitor: ['read'],
  // Live user monitor (tc-fun): read-only.
  UserMonitor: ['read'],
  Allowances: ['read', 'grant', 'consume', 'create', 'update', 'delete'],
  Interest: ['read', 'create', 'update', 'delete'],
  // Horoscope (zodiac) catalog (tc-fun): fixed 12-sign catalog — icon/name
  // update only via the shuffly /admin/horoscopes admin API. No create/delete.
  Horoscope: ['read', 'update'],
  // Text screening terms and the review queue (shuffly family). No delete:
  // shuffly keeps every term and switches a wrong one off. `update` also
  // covers deciding a refused text.
  TextFilter: ['read', 'create', 'update'],
  Cosmetic: ['read', 'create', 'update', 'delete'],
  CoinHistory: ['read', 'grantTest'],
  Game: ['read'],
  RevenuecatSync: ['read', 'update'],
  RevenuecatConsole: ['read'],
  Announcement: ['read', 'create', 'update', 'delete'],
  MessageTemplate: ['read', 'create', 'update', 'delete'],
  MessageAutomation: ['read', 'create', 'update', 'delete'],
  User: ['read', 'create', 'update', 'delete'],
  // Panel operators — a different population from `User` (app members). Kept
  // apart because every moderator needs `read:User` to look a member up on
  // /users/app, and gating the staff directory on that grant would publish the
  // operator roster (usernames, emails, role assignments) to all of them.
  // Global, never app-scoped: an operator account is panel-wide.
  StaffUser: ['read', 'create', 'update', 'delete'],
  Role: ['read', 'create', 'update', 'delete', 'assign'],
  Audit: ['read'],
  // Live-app runtime config (system_config). High-privilege; admin-tier only.
  SystemConfig: ['read', 'update'],
  // Synthetic seed-user studio (tc-fun only). generate/commit → create;
  // edit/enable/disable → update; delete/purge → delete.
  SyntheticUsers: ['read', 'create', 'update', 'delete'],
  Quest: ['read', 'create', 'update', 'delete'],
  // Panel-authored in-app campaigns (shuffly family). HTML content per locale,
  // server-side targeting, frequency capping and rewards. Read also covers the
  // engagement report and the audience-count preview.
  Spotlight: ['read', 'create', 'update', 'delete'],
  // Shop catalog (tc-fun): atoms, packages, item tiers. Managed via the
  // shuffly /admin/shop/* admin API; super_admin (manage:all) holds it.
  Shop: ['read', 'create', 'update', 'delete'],
  // Gift catalog + transfers (tc-fun): catalog CRUD via the shuffly
  // /admin/gifts/* admin API; reorder = atomic full-set reorder; label =
  // assign/remove shop labels on a gift's spine row.
  Gift: ['read', 'create', 'update', 'delete', 'reorder', 'label'],
  // Profile-photo moderation (tc-fun): read the upload history + slot state;
  // update = manual verify/reject of a pending upload (DB-direct — ADR 0030).
  ProfileImage: ['read', 'update'],
  // Cross-capability report surfaces. Reports read the apps and never write to
  // them. A Report grant is NOT sufficient authority over the underlying data —
  // the item report intersects its sources with the caller's per-capability
  // grants. `update` edits the panel-local coin pack list behind the store
  // report's estimated value; no system role holds it, so it stays super_admin's.
  Report: ['read', 'update'],
  // Partner conference monitor (tc-fun): live IVR room state, phone-number
  // moderation, and — from Phase 2 — room audio.
  Conference: ['read', 'moderate', 'listen', 'broadcast'],
  // Capability-scoped punishments (chat mute, room mute, ...) — deliberately
  // NOT `Ban`. Granting "can mute" must not grant "can ban the account", which
  // is the whole difference between a content moderator and a T&S lead.
  // `delete` is the lift. Account bans and scope-bans stay on `Ban`: same
  // authority, different reach.
  Restriction: ['read', 'create', 'delete'],
  // Room panic incidents (shuffly family): read the live queue + history;
  // update = claim and resolve, which shuffly echoes to the live-ops Telegram
  // thread.
  RoomIncident: ['read', 'update'],
  // 1-1 user reports (shuffly family): read the grouped queue + history;
  // update = decide (dismiss / actioned); reveal = unmask a phone caller's
  // number, audited on every use. NOT a sanction grant — acting on a report
  // still asks Ban / Restriction.
  UserReport: ['read', 'update', 'reveal'],
  // Live-ops attribution report. Deliberately no grantable rows: only
  // super_admin (manage:all) reaches it. Panel-wide, never app-scoped.
  LiveOps: [],
});

/** Flat list of every (action, subject) pair as a `${action}:${subject}` key. */
export type PermissionKey = `${Action}:${Subject}`;

export function listPermissionKeys(): readonly PermissionKey[] {
  const out: PermissionKey[] = [];
  for (const subject of SUBJECTS) {
    for (const action of PERMISSIONS[subject]) {
      out.push(`${action}:${subject}`);
    }
  }
  return out;
}

/** Subjects that support per-app scoping (a `taksim-chat` operator etc.). */
export const APP_SCOPED_SUBJECTS: ReadonlySet<Subject> = new Set([
  'Vip',
  'Sms',
  'Otp',
  'Ftu',
  'Ban',
  'Balance',
  'VoiceRecord',
  'VoiceRoom',
  'CallMonitor',
  'UserMonitor',
  'Allowances',
  'Interest',
  'Horoscope',
  'TextFilter',
  'Cosmetic',
  'CoinHistory',
  'Game',
  'RevenuecatSync',
  'RevenuecatConsole',
  'Announcement',
  'MessageTemplate',
  'MessageAutomation',
  // Per-app so an app_admin edits only its own app's runtime config; the two
  // global admin roles (super_admin via manage:all, ops_admin) edit any app's.
  'SystemConfig',
  'SyntheticUsers',
  'Quest',
  'Spotlight',
  'Shop',
  'Gift',
  'ProfileImage',
  'Report',
  'Conference',
  'Restriction',
  'RoomIncident',
  'UserReport',
  // App users are per-app: three of the four `read:User` routes resolve an
  // `appFrom` (list, filter-options, get-one). Only the cross-app caller
  // search (`GET /app-users/search`) asks globally, which is the correct thing
  // to withhold from an app-scoped moderator. `StaffUser` is deliberately NOT
  // here — a panel operator account is panel-wide.
  'User',
]);

/**
 * Subject → UI group. Mirrors the sidebar taxonomy so the role matrix reads
 * like the panel an operator will actually see.
 *
 * Every `SUBJECTS` entry must appear in exactly one group — `permissions
 * .contract.test.ts` fails the build otherwise, so a new subject cannot be
 * added without being placed.
 */
export const SUBJECT_GROUPS = [
  'users',
  'moderation',
  'rooms',
  'messaging',
  'economy',
  'catalog',
  'reports',
  'admin',
] as const;
export type SubjectGroup = (typeof SUBJECT_GROUPS)[number];

export const SUBJECT_GROUP: Readonly<Record<Subject, SubjectGroup>> = Object.freeze({
  all: 'admin',

  User: 'users',
  StaffUser: 'admin',
  UserMonitor: 'users',
  SyntheticUsers: 'users',
  ProfileImage: 'moderation',

  Ban: 'moderation',
  Restriction: 'moderation',
  RoomIncident: 'moderation',
  UserReport: 'moderation',
  VoiceRecord: 'moderation',

  VoiceRoom: 'rooms',
  Conference: 'rooms',
  CallMonitor: 'rooms',
  Game: 'rooms',

  Sms: 'messaging',
  Otp: 'messaging',
  Announcement: 'messaging',
  MessageTemplate: 'messaging',
  MessageAutomation: 'messaging',

  Vip: 'economy',
  Balance: 'economy',
  CoinHistory: 'economy',
  Allowances: 'economy',
  Quest: 'economy',
  Spotlight: 'messaging',
  Shop: 'economy',
  RevenuecatSync: 'economy',
  RevenuecatConsole: 'economy',
  Ftu: 'economy',

  Cosmetic: 'catalog',
  Gift: 'catalog',
  Interest: 'catalog',
  Horoscope: 'catalog',
  TextFilter: 'catalog',

  Report: 'reports',
  LiveOps: 'reports',

  Role: 'admin',
  Audit: 'admin',
  SystemConfig: 'admin',
});

/** Subjects in a group, in `SUBJECTS` order. */
export function subjectsInGroup(group: SubjectGroup): readonly Subject[] {
  return SUBJECTS.filter((s) => SUBJECT_GROUP[s] === group);
}

/**
 * The subjects of a permission set that can only ever be granted globally.
 *
 * A scoped grant on one of these matches nothing: `hasGrant` with a null
 * `appId` is satisfied only by global grants, so assigning (say) `auditor`
 * scoped to one app leaves the operator with zero Audit access. The role
 * responses carry this so the assignment dialog can name what would go dead
 * before an operator creates an assignment that silently does nothing.
 *
 * `all` is excluded: a scoped `manage:all` still satisfies every app-scoped
 * ask, so scoping it narrows the role rather than killing it — that is a
 * legitimate way to spell "admin of one app", not a mistake to warn about.
 */
export function globalOnlySubjects(
  permissions: readonly { readonly subject: Subject }[],
): readonly Subject[] {
  const out: Subject[] = [];
  for (const { subject } of permissions) {
    if (subject === 'all') continue;
    if (APP_SCOPED_SUBJECTS.has(subject)) continue;
    if (out.includes(subject)) continue;
    out.push(subject);
  }
  return out;
}
